import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_KOMMENTARE } from "../makros";

export const lektionKommentare: Lesson = {
  id: "kommentare",
  title: "Kommentare",
  summary: "Rückmeldungen an der richtigen Stelle – statt in einer E-Mail mit „Folie 7, zweiter Punkt“.",
  durationMinutes: 8,
  learningGoals: [
    "Kommentare an Folien und Objekte setzen",
    "Auf Kommentare antworten und sie erledigen",
    "Vor dem Versand alle Kommentare entfernen",
  ],
  steps: [
    {
      id: "kommentar",
      title: "Kommentieren",
      explanation: [
        "„Überprüfen“ → „Neuer Kommentar“ (oder Strg + Alt + M) hängt einen Kommentar an die " +
          "markierte Form oder – ohne Markierung – an die Folie. Rechts öffnet sich der " +
          "Kommentarbereich; mit @Name wird eine Person erwähnt und benachrichtigt, wenn die " +
          "Datei in OneDrive oder SharePoint liegt.",
        "Wie in Word gilt: Ein Kommentar gehört an die Stelle, um die es geht. Eine " +
          "Rückmeldung per E-Mail mit Folienangabe muss erst gesucht werden – ein Kommentar " +
          "steht schon dort.",
      ],
      keyPoints: [
        "Überprüfen → Neuer Kommentar, Strg + Alt + M.",
        "Form markieren = Kommentar hängt an der Form.",
        "@Name erwähnt eine Person.",
      ],
      task: {
        instruction: "Setze auf einer Folie einen Kommentar an ein Objekt.",
        hint: "Form markieren, Überprüfen → Neuer Kommentar, Text schreiben, Senden (Strg + Enter).",
        macro: MAKRO_KOMMENTARE,
        check: () =>
          pruefeMitMakro(MAKRO_KOMMENTARE, (bericht) =>
            fromCriteria([criterion(`Kommentare in der Präsentation: ${bericht.zahl("kommentare") ?? 0}`, (bericht.zahl("kommentare") ?? 0) > 0)], {
              success: "Die Rückmeldung steht genau dort, wo sie hingehört.",
              failure: "In der Präsentation gibt es noch keinen Kommentar.",
            }),
          ),
      },
    },
    {
      id: "antworten",
      title: "Antworten und erledigen",
      explanation: [
        "Unter jedem Kommentar steht das Antwortfeld. So bleibt eine Diskussion an einem Ort. " +
          "Ist die Sache geklärt, markiert „Thread auflösen“ (oder „Erledigt“) den Kommentar " +
          "als erledigt – er bleibt nachvollziehbar, stört aber nicht mehr.",
        "Vor dem Versand gehören alle Kommentare aus der Datei: „Überprüfen“ → „Löschen“ → " +
          "„Alle Kommentare in dieser Präsentation löschen“ – oder die Dokumentprüfung aus " +
          "dem Modul „Export und Weitergabe“.",
      ],
      keyPoints: [
        "Antworten statt neuer Kommentare.",
        "Thread auflösen, wenn es geklärt ist.",
        "Vor dem Versand alle Kommentare löschen.",
      ],
      task: {
        instruction: "Antworte auf deinen Kommentar.",
        hint: "Im Kommentarbereich unter dem Kommentar in „Antworten“ klicken.",
        macro: MAKRO_KOMMENTARE,
        check: () =>
          pruefeMitMakro(MAKRO_KOMMENTARE, (bericht) =>
            fromCriteria([criterion(`Antworten auf Kommentare: ${bericht.zahl("antworten") ?? 0}`, (bericht.zahl("antworten") ?? 0) > 0)], {
              success: "Die Diskussion bleibt an einem Ort.",
              failure: "Es gibt noch keine Antwort auf einen Kommentar.",
              hint: "Ein zweiter, eigenständiger Kommentar ist keine Antwort.",
            }),
          ),
      },
    },
  ],
};
